import { ActionIcon, Avatar, Box, Group, Menu, Text, Tooltip, UnstyledButton, useComputedColorScheme, useMantineColorScheme } from '@mantine/core';
import { IconLogout, IconMoon, IconSelector, IconSun, IconUser } from '@tabler/icons-react';
import { useClientAuth } from '../../auth/index.js';
import { accountView } from './accountView.js';

/**
 * The foot of the sidebar: who is signed in, the colour-scheme toggle, and the account menu.
 * The toggle lives here and nowhere else. Under the stub there is no session to end, so the
 * block opens nothing but still carries the toggle.
 */
export const AccountBlock = ({ name, email }: { name: string | null | undefined; email: string | null | undefined }) => {
    const auth = useClientAuth();
    const view = accountView({ name, email, canSignOut: auth.canSignOut });

    const identity = (
        <Group gap="sm" wrap="nowrap" style={{ flex: 1, minWidth: 0 }}>
            <Avatar size={32} radius="xl" variant="light" color="gray">
                {view.initials ?? <IconUser size={16} stroke={1.7} />}
            </Avatar>
            <Box style={{ flex: 1, minWidth: 0 }}>
                <Text fz="sm" fw={500} truncate>{view.displayName}</Text>
                {view.secondary && <Text fz="xs" c="dimmed" truncate title={view.secondary}>{view.secondary}</Text>}
            </Box>
        </Group>
    );

    return (
        <Group gap={4} wrap="nowrap" p="xs">
            {view.interactive ? (
                <Menu width={220} shadow="md" position="top-start">
                    <Menu.Target>
                        <UnstyledButton aria-label={`Account: ${view.displayName}`} style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 4, borderRadius: 'var(--mantine-radius-md)' }}>
                            {identity}
                            <Box c="dimmed" lh={0}><IconSelector size={16} stroke={1.7} /></Box>
                        </UnstyledButton>
                    </Menu.Target>
                    <Menu.Dropdown>
                        <Menu.Item leftSection={<IconLogout size={16} stroke={1.7} />} onClick={() => void auth.signOut()}>Sign out</Menu.Item>
                    </Menu.Dropdown>
                </Menu>
            ) : (
                identity
            )}
            <ColorSchemeToggle />
        </Group>
    );
};

const ColorSchemeToggle = () => {
    const { toggleColorScheme } = useMantineColorScheme();
    const scheme = useComputedColorScheme('light');
    const label = scheme === 'dark' ? 'Light theme' : 'Dark theme';
    return (
        <Tooltip label={label} withArrow openDelay={400}>
            <ActionIcon variant="subtle" color="gray" size={28} onClick={toggleColorScheme} aria-label="Toggle colour scheme">
                {scheme === 'dark' ? <IconSun size={16} stroke={1.7} /> : <IconMoon size={16} stroke={1.7} />}
            </ActionIcon>
        </Tooltip>
    );
};
